import * as React from 'react';
import { scrollIntoView } from 'app/infrastructure/platform/browser-dom';
import { debounceRender } from 'ui/common/debounce-render';
import { ExpanderBackground } from 'ui/common/dialog-expander/expander-background/expander-background';
import { ContextMenu } from 'ui/tile/context-menu';
import { ButtonSecondary, ButtonTertiary, Permission, ValidationLevel } from 'ui/common';
import { TileProps, TileState } from 'ui/tile/tile';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { OpenStateInjectedProps, withOpenState } from 'ui/common/enhancers/with-open-state';
import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';
import { MdContentCopy, MdEdit } from 'react-icons/md';
import { ModalSize } from 'app/components/constants';
import { InputModalHost } from 'ui/common/prompt-confirmation/input-modal-host';
import { DefaultCopyOptions } from 'ui/tile/copy-options';
import { ElementLoader } from 'ui/common/element-loader/element-loader';
import styles from './compact-tile.scss';
class CompactTileComponent extends React.Component<TileProps & OpenStateInjectedProps, TileState> {
  private ref: HTMLElement;
  private MAX_EXTENDED_CONTEXT_MENU_ITEMS = 5;
  public static defaultProps: Partial<TileProps & OpenStateInjectedProps> = {
    enabledChildEntityOperations: { remove: false },
    textKeyLabel: ''
  };
  public state: Readonly<TileState> = {
    hideImmediately: false
  };

  private handleSelect = () => {
    this.setState({ hideImmediately: false }, () => {
      if (!this.props.expandAll && this.ref) {
        scrollIntoView(this.ref);
      }
      this.props.onSelectDocumentItem && this.props.onSelectDocumentItem();
      this.props.open();
    });
  };

  private handleEdit = () => {
    this.props.onEditDocumentItem && this.props.onEditDocumentItem();
  };

  private handleDelete = () => {
    this.props.onDeleteDocumentItem && this.props.onDeleteDocumentItem();
  };

  private handleClose = () => {
    this.setState({hideImmediately: true}, () =>
      this.props.onSelectDocumentItem?.(null)); //select null to clear params like 'TblAction/1' etc from the route
    this.props.close();
  };

  private renderEditDetailsButton = () => {
    const {shouldEditDocumentItem, textDetailsEditButton, shouldHideEditDocumentIcon} = this.props;   
    const onClickEditIcon = shouldEditDocumentItem ? this.handleEdit : this.handleSelect;

    return <>
      {shouldHideEditDocumentIcon ? null :
        <div className={`${styles.buttonContainer}`} title={textDetailsEditButton} onClick={onClickEditIcon}>
          <MdEdit className={`${styles.buttonIcons}`}/>
        </div>}
    </>;
  };

  private renderViewDetailsButton = () => {
    const {textShowDetails} = this.props;
    const childrenHasErrors = !!this.props.validationDescendants?.length && 
      this.props.validationDescendants.some(v => v.level === ValidationLevel.error);

    const childrenHasWarnings = !!this.props.validationDescendants?.length && 
      this.props.validationDescendants.some(v => v.level === ValidationLevel.warning);

    const validationClass = childrenHasErrors ?
      styles.hasChildErrors : (childrenHasWarnings ? styles.hasChildWarnings : '');
  
    return <>
      <div className={`${styles.buttonContainer}`}>
        <ButtonTertiary classNames={`${styles.detailsButton} ${validationClass}`} onClick={this.handleSelect}>
          {textShowDetails}
        </ButtonTertiary>
      </div>
    </>;
  };

  private onSubmitDocument = () => {
    this.props.onSubmitDocument && this.props.onSubmitDocument(false);
    this.handleClose();
  };

  private addRef = (el: HTMLDivElement | null) => {
    this.ref = el;
  };

  public render() {
    const {
      componentId,
      details,
      children,
      activate,
      primaryKey,
      showPrimaryKey = true,
      showDetailsButton = true,
      showDetailsEditButton = false,
      textKeyLabel,
      textDone,
      textDetailsEditButton,
      print,
      expandAll,
      isOpen,
      extendedContextMenuOperations,
      textSubmit,
      textSubmitInfo,
      shouldRenderSubmitButton = false,
      onCopyDocumentItem,
      copyIterator,
      enabledChildEntityOperations,
      fetching,
      disabledOverlay,
      showDetailsHeader = false
    } = this.props;
    // Detail dialog styles and title when primaryKey prop is provided
    const primaryKeyValue = (primaryKey && primaryKey.value) || '';
    const dialogTitle = `${textKeyLabel} #${primaryKeyValue}`;

    const canActivate = showDetailsButton && !this.state.hideImmediately;
    const isActivated = canActivate && activate;
    const shouldRenderOpenDetailView = details && (print || expandAll || isActivated);
    const shouldRenderDetailsEditButton = shouldRenderOpenDetailView && showDetailsEditButton;
      
    const detailsEditButton = shouldRenderDetailsEditButton ?
      <ButtonSecondary classNames={styles.editButton} onClick={this.handleEdit}>
          {textDetailsEditButton}
      </ButtonSecondary> : 
      shouldRenderSubmitButton ? <ButtonSecondary onClick={this.onSubmitDocument} textClose={textSubmitInfo}>
        {textSubmit}
      </ButtonSecondary> : null;

    // From above logical evaluations; construct a expander to display details
    const detailViewExpanded = shouldRenderOpenDetailView ?
                                <DialogExpander
                                  expandAll={true}
                                  onClose={this.handleClose}
                                  title={dialogTitle}
                                  hideTitle={expandAll}
                                  textDone={textDone}
                                  extraFooterActions={[detailsEditButton]}
                                  className="tile-details-view">
                                  {expandAll && <ContextMenu {...this.props} onDelete={this.handleDelete}/>}
                                  {details}
                                  {expandAll && <div className={styles.editButtonExpandAll}>{detailsEditButton}</div>}
                                </DialogExpander>
                              : null;

    const detailView = shouldRenderOpenDetailView ?
                        <ModalWrapper
                          headerTitle={dialogTitle}
                          showHeader={showDetailsHeader}
                          btnTitle={textDone}
                          onClose={this.handleClose}
                          onDone={this.handleClose}
                          isExtraFooterActionDisabled={false}
                          btnExtraFooterActionOverride={detailsEditButton}
                          size={ModalSize.xtraLarge}
                          textClose={textDone}
                          closeOnDone={false}
                          >
                          
                          {details}
                        </ModalWrapper>
                      : null;

    const editDetailsButton = showDetailsEditButton && details && this.renderEditDetailsButton();

    const wrapperStyle = `${styles.compactTileWrapper} ${shouldRenderOpenDetailView && !expandAll ? styles.activeTile : ''}`;

    if (expandAll && details) {
      // Only show details - no header, no context menu - no modal backdrop
      return <>
        <div id={componentId} className={`${wrapperStyle} compact compact-tile`} ref={this.addRef}>
          {detailViewExpanded}
          {isActivated && 
            <span className={styles.activatedDetailView}>
              {detailView}
            </span>}
        </div>
      </>;
    }

    const viewDetailsButton = showDetailsButton && details && this.renderViewDetailsButton();
    
    //overflow style for context menu(when there are too many items to renders as icons and text from extendedContextMenuOperations)
    const contextMenuOverflow = extendedContextMenuOperations?.length >= this.MAX_EXTENDED_CONTEXT_MENU_ITEMS || 
      (extendedContextMenuOperations?.filter(i=>i.text && !i.icon))?.length > 1;
    const hideContextMenu = !viewDetailsButton && !editDetailsButton && !extendedContextMenuOperations?.length;
    const contextMenuOverflowStyle = hideContextMenu ? undefined : (contextMenuOverflow ? styles.hideOnOverflow : styles.hideOnResize);
    
    //always render first element of extended context menu as icon or text if there is no details button and no details
    const showExtendedContextMenuFirstElementAsIconOrText  = !showDetailsButton && !details && !!extendedContextMenuOperations?.length;
    const extendedContextMenuFirstElementAsButton = showExtendedContextMenuFirstElementAsIconOrText &&
      <div className={styles.buttonContainer}
        title={extendedContextMenuOperations[0].icon ? extendedContextMenuOperations[0].text : undefined} 
        onClick={extendedContextMenuOperations[0].callback}>
        <span className={styles.buttonIcons}>
         {!!extendedContextMenuOperations[0]?.icon ? <span>{extendedContextMenuOperations[0].icon}</span> : 
          <span className={styles.extendedText}>{extendedContextMenuOperations[0].text}</span>}
        </span>
      </div>;
    
    //exclude first element from extended context menu if rendered as icon or text
    const filteredExtendedContextMenuOperations = showExtendedContextMenuFirstElementAsIconOrText ? 
      extendedContextMenuOperations?.slice(1) : extendedContextMenuOperations;
    
    const filteredExtendedContextMenuButtons = filteredExtendedContextMenuOperations?.map((operation, i) => {
      const title = !!operation.icon ? operation.text : undefined;
      return <div
        key={`button-${i}`}
        className={`${styles.buttonContainer} ${operation.type !== 'changeSequence' ? contextMenuOverflowStyle : ''}`}
        title={title} onClick={operation.callback}>
        <span className={styles.buttonIcons}>{operation.icon || operation.text}</span>
      </div>;
      }
    );

    const contextMenu = <ContextMenu {...this.props} className={styles.contextMenu} onDelete={this.handleDelete}
      extendedContextMenuOperations={filteredExtendedContextMenuOperations}/>;

    const filteredContextMenu = <ContextMenu {...this.props} className={styles.contextMenuFiltered} onDelete={this.handleDelete}
      extendedContextMenuOperations={undefined} disableCopy={true}/>;

    /* eslint-disable @typescript-eslint/no-shadow */
    const copyButton = onCopyDocumentItem &&
      <Permission requireAll="add,copy" permissions={enabledChildEntityOperations} isListElement={false}>
        <InputModalHost onContinue={(onCopyDocumentItem)} {...this.props}>
            { ({isOpen, onOpen, onContinue, onCancel}) => {
              return (
                <>
                  <div className={`${styles.buttonContainer} ${contextMenuOverflowStyle}`} title={copyIterator.copy} onClick={onOpen}>
                    <MdContentCopy className={`${styles.buttonIcons}`}/>

                  </div>
                  {isOpen && 
                    <DefaultCopyOptions 
                      {...this.props} 
                      handleContinue={onContinue} 
                      handleCancel={onCancel} 
                      translate={copyIterator}/>}
                </>
              );
            }}
        </InputModalHost>
      </Permission>;

    return <>
      {/* When expanded/open - render the close handle before the tile */}
      {shouldRenderOpenDetailView ? <div tabIndex={0} onFocus={this.handleClose}/> : ''}

      <div id={componentId} className={`${wrapperStyle} ${fetching && styles.componentLoader} compact compact-tile`}>
        <div className={`${styles.compactTile}`}>
          <div className={styles.btnIconsWrapper}>
            {viewDetailsButton}
            {editDetailsButton}
            {copyButton}
            {extendedContextMenuFirstElementAsButton}
            {filteredExtendedContextMenuButtons}
            {contextMenu}
            {filteredContextMenu}
          </div>
          <div className={`${styles.contentWrapper} ${showPrimaryKey ? styles.showKey : ''}`}>
            {children}
          </div>
        </div>
          {/* If expanded (active, expand-all, print) - renders the details */}
          {(isOpen || shouldRenderOpenDetailView) && detailView}
          {/* Modal backdrop */}
          {(shouldRenderOpenDetailView && !(print || expandAll)) && <ExpanderBackground onClick={this.handleClose}/>}
          {fetching && <div className={styles.elementLoader}>
            <ElementLoader />
          </div>}
          {disabledOverlay && <div className={styles.disabledOverlay} />}
      </div>
    </>;
  }
}

const t = withOpenState()(debounceRender(CompactTileComponent));
export {t as CompactTile};
